import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import QRCode from "qrcode";
import jsQR from "jsqr";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL?.endsWith("/api") ? BACKEND_URL : `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Per-competitor fields. `w` controls the grid span (out of 6).
// CMP scorecards preprint Firing Point / Classification / Category and don't use
// NRA #, so those are intentionally omitted here (see backend/labels.py).
const FIELDS = [
  { key: "name", label: "Name (Last, First, Middle)", w: 6 },
  { key: "address", label: "Mailing Address", w: 3 },
  { key: "city", label: "City", w: 2 },
  { key: "state", label: "State", w: 1 },
  { key: "zip", label: "Zip", w: 2 },
  { key: "phone", label: "Telephone", w: 2 },
  { key: "email", label: "Email", w: 2 },
  { key: "cmp_number", label: "CMP Competitor ID#", w: 2 },
  { key: "birthdate", label: "Birthdate", w: 1 },
  { key: "gender", label: "Gender", w: 1 },
  { key: "fees", label: "Fees", w: 1 },
];

const REMEMBER_KEY = "mtLabelMe"; // localStorage: this device's saved person

// Sheet geometry — mirrors backend/labels.py (STRIP_HEIGHT_IN + the cut-guide
// gap vs. usable page height) so we can auto-fill whole sheets.
const STRIP_HEIGHT_IN = 1.85; // sized to cover the card's fill-in section
const CUT_GAP_IN = 0.3; // .cutline margin: 0.15in top + 0.15in bottom
const PAGE_MARGIN_IN = 0.5;
const PAGE_HEIGHT_IN = { letter: 11, legal: 14 };
const labelsPerPage = (paper) => {
  const usable = (PAGE_HEIGHT_IN[paper] || 11) - 2 * PAGE_MARGIN_IN + CUT_GAP_IN;
  return Math.max(1, Math.floor(usable / (STRIP_HEIGHT_IN + CUT_GAP_IN)));
};

const blankCompetitor = () => ({
  ...FIELDS.reduce((o, f) => ({ ...o, [f.key]: "" }), {}),
  copies: "1",
  unit: "labels", // "labels" | "sheets"
});

// Keep only known, non-empty label fields from an arbitrary object.
const pickFields = (obj) =>
  FIELDS.reduce((o, { key }) => {
    const v = (obj?.[key] ?? "").toString().trim();
    if (v) o[key] = v;
    return o;
  }, {});

// A Match Track label QR is JSON: {"mtlabel":1,"f":{name,...}}. This marker lets
// us ignore unrelated QR codes and never act on random data.
const encodePayload = (c) => JSON.stringify({ mtlabel: 1, f: pickFields(c) });
const decodePayload = (text) => {
  try {
    const o = JSON.parse(text);
    if (o && o.mtlabel && o.f && typeof o.f === "object") return pickFields(o.f);
  } catch (_) {}
  return null;
};

const ScorecardLabels = () => {
  const [mode, setMode] = useState("strips"); // "strips" | "card"
  const [paper, setPaper] = useState("letter"); // "letter" | "legal"
  const [matchDate, setMatchDate] = useState("");
  const [matchLocation, setMatchLocation] = useState("");
  const [competitors, setCompetitors] = useState([blankCompetitor()]);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  // QR + scan + remember (all client-side; nothing is sent to the server).
  const [qrView, setQrView] = useState(null); // { dataUrl, name }
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState("");
  const [scanCount, setScanCount] = useState(0);
  const [flash, setFlash] = useState(false); // green "✓ Added" pulse on a scan
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastScanRef = useRef({ text: "", at: 0 });
  const audioRef = useRef(null);

  // Obvious "scan landed" feedback: beep + screen flash + (mobile) vibrate.
  const signalScan = useCallback(() => {
    try {
      let ctx = audioRef.current;
      if (!ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) ctx = audioRef.current = new AC();
      }
      if (ctx) {
        if (ctx.state === "suspended") ctx.resume();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
        o.connect(g);
        g.connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.2);
      }
    } catch (_) {}
    if (navigator.vibrate) {
      try {
        navigator.vibrate(80);
      } catch (_) {}
    }
    setFlash(true);
    setTimeout(() => setFlash(false), 420);
  }, []);

  // ---- Pre-fill on load: #d=<json> link wins, else this device's saved person.
  useEffect(() => {
    let f = null;
    const m = /[#&]d=([^&]+)/.exec(window.location.hash || "");
    if (m) {
      try {
        f = pickFields(JSON.parse(decodeURIComponent(m[1])));
      } catch (_) {}
    }
    if (!f || !Object.keys(f).length) {
      try {
        const s = localStorage.getItem(REMEMBER_KEY);
        if (s) f = pickFields(JSON.parse(s));
      } catch (_) {}
    }
    if (f && Object.keys(f).length) {
      setCompetitors((prev) => [{ ...prev[0], ...f }, ...prev.slice(1)]);
    }
  }, []);

  const perPage = labelsPerPage(paper);
  const competitorLabelCount = (c) => {
    const n = Math.max(1, parseInt(c.copies, 10) || 1);
    return Math.min(400, c.unit === "sheets" ? n * perPage : n);
  };
  const totalLabels = competitors.reduce(
    (sum, c) => sum + competitorLabelCount(c),
    0
  );

  const update = (idx, key, value) =>
    setCompetitors((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [key]: value } : c))
    );

  const addCompetitor = () =>
    setCompetitors((prev) => [...prev, blankCompetitor()]);

  const removeCompetitor = (idx) =>
    setCompetitors((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)
    );

  const firstIsBlank = (list) =>
    list.length === 1 && FIELDS.every(({ key }) => !(list[0][key] || "").trim());

  // Append a scanned/loaded person (reuse the initial blank row if untouched).
  const addPerson = useCallback((f) => {
    const filled = { ...blankCompetitor(), ...f };
    setCompetitors((prev) => (firstIsBlank(prev) ? [filled] : [...prev, filled]));
  }, []);

  // ---- QR generate (client-side) --------------------------------------------
  const makeQr = async (idx) => {
    const c = competitors[idx];
    if (!Object.keys(pickFields(c)).length) {
      setScanMsg("Fill in some fields first, then make a QR.");
      return;
    }
    try {
      const dataUrl = await QRCode.toDataURL(encodePayload(c), {
        margin: 1,
        width: 320,
        errorCorrectionLevel: "M",
      });
      setQrView({ dataUrl, name: c.name || `competitor-${idx + 1}` });
    } catch (e) {
      setScanMsg("Could not build QR.");
    }
  };
  const downloadQr = () => {
    if (!qrView) return;
    const a = document.createElement("a");
    a.href = qrView.dataUrl;
    a.download = `mt-label-${(qrView.name || "competitor").replace(/[^\w-]+/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ---- Webcam batch scan ----------------------------------------------------
  const stopScan = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  const tick = useCallback(() => {
    const v = videoRef.current;
    const canvas = canvasRef.current;
    if (v && canvas && v.readyState === v.HAVE_ENOUGH_DATA) {
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height, {
        inversionAttempts: "dontInvert",
      });
      if (code && code.data) {
        const now = Date.now();
        const isRepeat =
          code.data === lastScanRef.current.text &&
          now - lastScanRef.current.at < 2500;
        if (!isRepeat) {
          const f = decodePayload(code.data);
          lastScanRef.current = { text: code.data, at: now };
          if (f && Object.keys(f).length) {
            addPerson(f);
            setScanCount((n) => n + 1);
            setScanMsg(`Added ${f.name || "competitor"} ✓`);
            signalScan();
          } else {
            setScanMsg("That QR isn't a Match Track label QR.");
          }
        }
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [addPerson, signalScan]);

  const startScan = async () => {
    setScanMsg("");
    setScanCount(0);
    if (!navigator.mediaDevices?.getUserMedia) {
      setScanMsg("This browser has no camera access.");
      return;
    }
    setScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      const v = videoRef.current;
      v.srcObject = stream;
      v.setAttribute("playsinline", "true");
      await v.play();
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setScanMsg("Camera unavailable or permission denied.");
      setScanning(false);
    }
  };

  useEffect(() => () => stopScan(), [stopScan]); // stop camera on unmount

  // ---- Remember on this device / pre-fill link ------------------------------
  const rememberMe = () => {
    const f = pickFields(competitors[0]);
    if (!Object.keys(f).length) {
      setScanMsg("Fill in competitor 1 first.");
      return;
    }
    localStorage.setItem(REMEMBER_KEY, JSON.stringify(f));
    setScanMsg("Saved on this device ✓ (only in this browser)");
  };
  const forgetMe = () => {
    localStorage.removeItem(REMEMBER_KEY);
    setScanMsg("Cleared from this device.");
  };
  const copyPrefillLink = async () => {
    const f = pickFields(competitors[0]);
    if (!Object.keys(f).length) {
      setScanMsg("Fill in competitor 1 first.");
      return;
    }
    const link = `${window.location.origin}${window.location.pathname}#d=${encodeURIComponent(
      JSON.stringify(f)
    )}`;
    try {
      await navigator.clipboard.writeText(link);
      setScanMsg("Pre-fill link copied — bookmark it to auto-fill next time.");
    } catch (_) {
      window.prompt("Copy your pre-fill link:", link);
    }
  };

  const download = async () => {
    setError("");
    setDownloading(true);
    try {
      const payloadCompetitors = [];
      competitors.forEach((c) => {
        const count = competitorLabelCount(c);
        for (let i = 0; i < count; i++) {
          const { copies: _c, unit: _u, ...rest } = c;
          payloadCompetitors.push({
            ...rest,
            date: c.date || matchDate,
            location: c.location || matchLocation,
          });
        }
      });
      const res = await axios.post(
        `${API}/labels/pdf`,
        { mode, paper, competitors: payloadCompetitors },
        { headers: authHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `scorecard-labels-${mode}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Label PDF error:", e?.response?.data || e);
      setError("Could not generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const inputCls =
    "w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400";
  const spanCls = {
    1: "col-span-2 sm:col-span-1",
    2: "col-span-3 sm:col-span-2",
    3: "col-span-6 sm:col-span-3",
    6: "col-span-6",
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900">Scorecard Labels</h1>
      <p className="text-gray-600 mt-1 text-sm">
        Fill in each competitor, then download a print-ready PDF that covers the top
        entry area of the CMP card. Print on full-sheet label stock (e.g. Avery 8165)
        and cut between the strips.
      </p>

      {/* Privacy-first tools: QR & scan run entirely in your browser — nothing is
          stored on the server. */}
      <div className="mt-4 flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <span className="text-sm font-semibold text-blue-900 mr-1">Fast fill:</span>
        <button
          type="button"
          onClick={scanning ? stopScan : startScan}
          className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
        >
          {scanning ? "Stop camera" : "Scan QR (camera)"}
        </button>
        <button type="button" onClick={rememberMe} className="px-3 py-1.5 rounded-lg text-sm border border-blue-300 bg-white hover:bg-blue-50">
          Remember me on this device
        </button>
        <button type="button" onClick={copyPrefillLink} className="px-3 py-1.5 rounded-lg text-sm border border-blue-300 bg-white hover:bg-blue-50">
          Copy pre-fill link
        </button>
        <button type="button" onClick={forgetMe} className="px-2 py-1.5 rounded-lg text-xs text-blue-700 underline">
          forget me
        </button>
        {scanMsg && <span className="text-sm text-blue-800 ml-1">{scanMsg}</span>}
        <span className="w-full text-xs text-blue-700/80">
          Scan a competitor’s saved QR to add them (keep scanning for a whole squad —
          batch mode). Nothing you enter here is saved on the server; “Remember me” and
          the pre-fill link stay on your own device.
        </span>
      </div>

      {/* Camera preview (only while scanning) */}
      {scanning && (
        <div className="mt-3 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">
              Point the camera at a Match Track QR
            </span>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center rounded-full bg-green-100 text-green-800 text-sm font-bold px-3 py-1">
                Added: {scanCount}
              </span>
              <button type="button" onClick={stopScan} className="text-sm text-red-600">
                Done
              </button>
            </div>
          </div>
          <div className="relative w-full max-w-md">
            <video ref={videoRef} className="w-full rounded bg-black" muted playsInline />
            {/* green flash + badge on each successful scan */}
            <div
              className={`pointer-events-none absolute inset-0 rounded ring-4 transition-opacity duration-300 ${
                flash ? "opacity-100 bg-green-400/40 ring-green-400" : "opacity-0 ring-transparent"
              }`}
            />
            {flash && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="rounded-full bg-green-600 text-white text-2xl font-extrabold px-5 py-2 shadow-lg">
                  ✓ Added
                </span>
              </div>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}

      {/* QR viewer (this competitor’s own QR to save/share) */}
      {qrView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setQrView(null)}>
          <div className="bg-white rounded-lg p-5 max-w-xs w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-gray-800 mb-2">
              {qrView.name}’s label QR
            </div>
            <img src={qrView.dataUrl} alt="Label QR code" className="mx-auto w-56 h-56" />
            <p className="text-xs text-gray-500 mt-2">
              Save this to your phone. At the match, staff scan it to fill your label —
              no account needed, nothing stored.
            </p>
            <div className="mt-3 flex justify-center gap-2">
              <button type="button" onClick={downloadQr} className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700">
                Download PNG
              </button>
              <button type="button" onClick={() => setQrView(null)} className="px-4 py-1.5 rounded-lg text-sm border border-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global controls */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
        <label className="text-sm">
          <span className="block text-gray-500 text-xs uppercase">Layout</span>
          <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="strips">Sticker strips (cut apart)</option>
            <option value="card">Whole area (one per page)</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-500 text-xs uppercase">Paper</span>
          <select className={inputCls} value={paper} onChange={(e) => setPaper(e.target.value)}>
            <option value="letter">Letter</option>
            <option value="legal">Legal</option>
          </select>
          <span className="mt-1 block text-xs text-gray-500">
            ~{perPage} labels / {paper} page
          </span>
        </label>
        <div className="grid grid-cols-2 gap-2 col-span-2 md:col-span-1">
          <label className="text-sm">
            <span className="block text-gray-500 text-xs uppercase">Match date</span>
            <input className={inputCls} value={matchDate} onChange={(e) => setMatchDate(e.target.value)} placeholder="8/2/26" />
          </label>
          <label className="text-sm">
            <span className="block text-gray-500 text-xs uppercase">Location</span>
            <input className={inputCls} value={matchLocation} onChange={(e) => setMatchLocation(e.target.value)} placeholder="Marengo, OH" />
          </label>
        </div>
      </div>

      {/* Competitor cards */}
      {competitors.map((c, idx) => (
        <div key={idx} className="mt-4 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-gray-700 text-sm">
              Competitor {idx + 1}
            </span>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                Print
                <input
                  type="number"
                  min="1"
                  max={c.unit === "sheets" ? 20 : 200}
                  value={c.copies ?? "1"}
                  onChange={(e) => update(idx, "copies", e.target.value)}
                  className="w-16 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <select
                  value={c.unit || "labels"}
                  onChange={(e) => update(idx, "unit", e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="labels">labels</option>
                  <option value="sheets">full sheets</option>
                </select>
              </label>
              <span className="text-xs text-gray-400">
                = {competitorLabelCount(c)} label{competitorLabelCount(c) === 1 ? "" : "s"}
              </span>
              <button
                type="button"
                onClick={() => makeQr(idx)}
                className="text-blue-600 text-sm hover:underline"
              >
                QR
              </button>
              <button
                type="button"
                onClick={() => removeCompetitor(idx)}
                disabled={competitors.length === 1}
                className="text-red-600 text-sm disabled:text-gray-300"
              >
                Remove
              </button>
            </div>
          </div>
          <div className="grid grid-cols-6 gap-2">
            {FIELDS.map((f) => (
              <label key={f.key} className={`text-sm ${spanCls[f.w]}`}>
                <span className="block text-gray-500 text-xs">{f.label}</span>
                <input
                  className={inputCls}
                  value={c[f.key] || ""}
                  onChange={(e) => update(idx, f.key, e.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={addCompetitor}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
        >
          + Add competitor
        </button>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
        >
          {downloading
            ? "Generating…"
            : `Download PDF (${totalLabels} label${totalLabels === 1 ? "" : "s"}, ~${Math.max(1, Math.ceil(totalLabels / perPage))} ${paper} page${Math.ceil(totalLabels / perPage) === 1 ? "" : "s"})`}
        </button>
        {error && <span className="text-red-600 text-sm">{error}</span>}
      </div>
    </div>
  );
};

export default ScorecardLabels;
